import { getToken, clearToken } from '../utils/authToken';
/**
 * PlatformStats - Admin-only component showing platform statistics
 * Displays total registered users and active sessions in real-time
 */
import { useState, useEffect, useRef } from 'react';
import { API_URL, API } from '../config';
import { Users, Activity, TrendingUp, BarChart3 } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import AdminDashboard from './AdminDashboard';
import axios from 'axios';


// Heartbeat interval (send every 2 minutes to track active session)
const HEARTBEAT_INTERVAL = 2 * 60 * 1000;
// Stats refresh interval (every 30 seconds for real-time updates)
const STATS_REFRESH_INTERVAL = 30 * 1000;

const PlatformStats = () => {
  const { language } = useLanguage();
  const [isAdmin, setIsAdmin] = useState(false);
  const [stats, setStats] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [showDashboard, setShowDashboard] = useState(false);
  const heartbeatRef = useRef(null);
  const statsRefreshRef = useRef(null);
  
  const txt = {
    es: {
      users: 'Usuarios',
      active: 'Activos',
      new24h: 'Nuevos (24h)',
      tooltip: 'Estadísticas de la plataforma',
      openDashboard: 'Ver panel de administración',
    },
    en: {
      users: 'Users',
      active: 'Active',
      new24h: 'New (24h)',
      tooltip: 'Platform statistics',
      openDashboard: 'View admin dashboard',
    }
  };
  const t = txt[language] || txt.es;
  
  // Check if user is admin on mount
  useEffect(() => {
    checkAdminStatus();
    
    return () => {
      // Cleanup intervals on unmount
      if (heartbeatRef.current) clearInterval(heartbeatRef.current);
      if (statsRefreshRef.current) clearInterval(statsRefreshRef.current);
    };
  }, []);
  
  // Start heartbeat and stats refresh when admin is confirmed
  useEffect(() => {
    if (isAdmin) {
      fetchStats();
      
      // Start heartbeat for session tracking
      sendHeartbeat();
      heartbeatRef.current = setInterval(sendHeartbeat, HEARTBEAT_INTERVAL);
      
      // Start stats refresh for real-time updates
      statsRefreshRef.current = setInterval(fetchStats, STATS_REFRESH_INTERVAL);
    }
  }, [isAdmin]);
  
  const checkAdminStatus = async () => {
    try {
      const token = getToken();
      if (!token || token === 'guest') {
        setIsAdmin(false);
        setIsLoading(false);
        return;
      }
      
      const response = await axios.get(`${API}/stats/check-admin?authorization=${token}`);
      setIsAdmin(response.data.is_admin === true);
    } catch (error) {
      console.error('Error checking admin status:', error);
      setIsAdmin(false);
    } finally {
      setIsLoading(false);
    }
  };
  
  const sendHeartbeat = async () => {
    try {
      const token = getToken();
      if (!token || token === 'guest') return;
      
      await axios.post(`${API}/stats/heartbeat?authorization=${token}`);
    } catch (error) {
      // Silent fail - heartbeat is not critical
    }
  };
  
  const fetchStats = async () => {
    try {
      const token = getToken();
      if (!token || token === 'guest') return;
      
      const response = await axios.get(`${API}/stats/platform?authorization=${token}`);
      setStats(response.data);
    } catch (error) {
      console.error('Error fetching platform stats:', error);
    }
  };
  
  // Don't render anything if not admin or still loading
  if (isLoading || !isAdmin || !stats) return null;
  
  return (
    <>
      <div 
        className="flex items-center gap-2 px-2 py-1 rounded-lg bg-gradient-to-r from-emerald-900/30 to-teal-900/30 border border-emerald-500/30"
        title={t.tooltip}
        data-testid="platform-stats"
      >
        {/* Total Users */}
        <div 
          className="flex items-center gap-1.5 px-2 py-0.5"
          data-testid="total-users-counter"
        >
          <Users className="w-3.5 h-3.5 text-emerald-400" />
          <span className="text-xs font-semibold text-emerald-300">
            {stats.total_users?.toLocaleString() || 0}
          </span>
          <span className="text-[10px] text-emerald-500 hidden sm:inline">
            {t.users}
          </span>
        </div>
        
        {/* Divider */}
        <div className="w-px h-4 bg-emerald-500/30" />
        
        {/* Active Sessions */}
        <div 
          className="flex items-center gap-1.5 px-2 py-0.5"
          data-testid="active-sessions-counter"
        >
          <Activity className="w-3.5 h-3.5 text-cyan-400 animate-pulse" />
          <span className="text-xs font-semibold text-cyan-300">
            {stats.active_sessions?.toLocaleString() || 0}
          </span>
          <span className="text-[10px] text-cyan-500 hidden sm:inline">
            {t.active}
          </span>
        </div>
        
        {/* New Users 24h - Hidden on small screens */}
        {stats.new_users_24h > 0 && (
          <>
            <div className="hidden md:block w-px h-4 bg-emerald-500/30" />
            <div 
              className="hidden md:flex items-center gap-1.5 px-2 py-0.5"
              data-testid="new-users-counter"
            >
              <TrendingUp className="w-3.5 h-3.5 text-amber-400" />
              <span className="text-xs font-semibold text-amber-300">
                +{stats.new_users_24h}
              </span>
              <span className="text-[10px] text-amber-500">
                {t.new24h}
              </span>
            </div>
          </>
        )}
        
        {/* Divider */}
        <div className="w-px h-4 bg-emerald-500/30" />
        
        {/* Dashboard Button */}
        <button
          onClick={() => setShowDashboard(true)}
          className="flex items-center gap-1.5 px-2 py-0.5 hover:bg-emerald-500/20 rounded transition-colors"
          title={t.openDashboard}
          data-testid="open-admin-dashboard-btn"
        >
          <BarChart3 className="w-3.5 h-3.5 text-indigo-400" />
          <span className="text-[10px] text-indigo-400 hidden lg:inline">
            Dashboard
          </span>
        </button>
      </div>
      
      {/* Admin Dashboard Modal */}
      <AdminDashboard
        isOpen={showDashboard}
        onClose={() => setShowDashboard(false)}
      />
    </>
  );
};

export default PlatformStats;
